import React, { useState, useEffect, useMemo, useRef } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import moment from "moment";
import XLSX from "xlsx";
import Datetime from "react-datetime";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import IconButton from "@material-ui/core/IconButton";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import HistoryIcon from "@material-ui/icons/History";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import BlockIcon from "@material-ui/icons/Block";
import AttachMoneyOutlinedIcon from "@material-ui/icons/AttachMoneyOutlined";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import Button from "designV2/components/Button";
import Field from "designV2/components/Field";
import MetricCard from "designV2/components/MetricCard";
import FilterDropdown from "designV2/components/FilterDropdown";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import DialogShell from "designV2/components/DialogShell";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import OrderDetails from "shopAdminViews/orders/OrderDetails";
import "./ordersHistory.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

// OrderDetails reads status, tracking.trackId, user.fullName, finalPrice,
// createdAt and cart.products, so none of those may be dropped.
// rejectedFor is new: on an archive screen, why an order was rejected is
// usually the reason someone is looking at it.
const ORDER_FIELDS = `
  _id
  user {
    _id
    fullName
    phoneNumber
  }
  shipmentModel
  finalPrice
  commission
  createdAt
  finished
  status
  rejectedFor
  trip {
    _id
    trackId
    origin {
      coordinates
    }
  }
  tracking {
    trackId
  }
  cart {
    products {
      quantity
      product {
        _id
        title
      }
    }
  }
`;

const GET_ORDERS = gql`
  query getOrdersHistoryByShopAdmin(
    $limit: Int
    $skip: Int
    $_id: ID
    $user: ID
    $shipmentModel: OrderShipmentModel
    $finalPrice: Float
    $commission: Float
    $finished: Boolean
    $status: OrderStatus
    $createdAt: Date
    $createdAtSort: Int
    $finalPriceSort: Int
    $passengerName: String
    $passengerPhoneNumber: String
    $tracking: OrderTrackingQueryInput
    $createdAtFrom: Date
  ) {
    getOrdersHistoryByShopAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: {
        _id: $_id
        user: $user
        shipmentModel: $shipmentModel
        finalPrice: $finalPrice
        commission: $commission
        finished: $finished
        status: $status
        createdAt: $createdAt
        passengerName: $passengerName
        passengerPhoneNumber: $passengerPhoneNumber
        tracking: $tracking
        createdAtFrom: $createdAtFrom
      }
      sort: { createdAt: $createdAtSort, finalPrice: $finalPriceSort }
    ) {
      ${ORDER_FIELDS}
    }
  }
`;

const GET_ORDERS_COUNT = gql`
  query getOrdersHistoryByShopAdminCount(
    $_id: ID
    $user: ID
    $shipmentModel: OrderShipmentModel
    $finalPrice: Float
    $commission: Float
    $finished: Boolean
    $status: OrderStatus
    $createdAt: Date
    $passengerName: String
    $passengerPhoneNumber: String
    $tracking: OrderTrackingQueryInput
    $createdAtFrom: Date
  ) {
    getOrdersHistoryByShopAdminCount(
      filters: {
        _id: $_id
        user: $user
        shipmentModel: $shipmentModel
        finalPrice: $finalPrice
        commission: $commission
        finished: $finished
        status: $status
        createdAt: $createdAt
        passengerName: $passengerName
        passengerPhoneNumber: $passengerPhoneNumber
        tracking: $tracking
        createdAtFrom: $createdAtFrom
      }
    )
  }
`;

// Aliased totals for the summary strip: three countDocuments in one
// round trip, unaffected by whichever page is currently loaded.
const GET_HISTORY_SUMMARY = gql`
  query getOrdersHistorySummary {
    total: getOrdersHistoryByShopAdminCount(filters: {})
    delivered: getOrdersHistoryByShopAdminCount(filters: { status: DELIVERED })
    rejected: getOrdersHistoryByShopAdminCount(filters: { status: REJECTED })
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];
const DEFAULT_PER_PAGE = 10;
const TRACK_ID_LENGTH = 10;

const STATUS_OPTIONS = [
  "ALL",
  "PENDING",
  "PAID",
  "SHIPPING",
  "DELIVERED",
  "REJECTED",
  "DELIVERY NOT ACCEPTED",
  "FINISHED DUE TO NOT PAYING",
  "PREPARING",
  "PREPARED",
];

const SHIPMENT_MODEL_OPTIONS = ["ALL", "SHOP", "SPARK"];
const FINISHED_OPTIONS = ["ALL", "FINISHED", "UNFINISHED"];

// Shared with the Orders queue and the dashboard so a status reads the
// same wherever it appears.
const STATUS_PRESENTATION = {
  PENDING: { label: "Awaiting payment", tone: "neutral" },
  PAID: { label: "New", tone: "info" },
  PREPARING: { label: "Preparing", tone: "warning" },
  PREPARED: { label: "Ready", tone: "accent" },
  SHIPPING: { label: "Shipping", tone: "info" },
  DELIVERED: { label: "Delivered", tone: "success" },
  REJECTED: { label: "Rejected", tone: "danger" },
  DELIVERY_NOT_ACCEPTED: { label: "Delivery refused", tone: "danger" },
  FINISHED_DUE_TO_NOT_PAYING: { label: "Unpaid", tone: "neutral" },
};

const REJECTION_LABELS = {
  packingDamaged: "Packing damaged",
  damagedForPacking: "Damaged in packing",
  differentProduct: "Wrong product",
  noReceiver: "No receiver",
  canceledByShopAdmin: "Cancelled by shop",
  deliveryWasNotAccepted: "Delivery not accepted",
};

const EXCEL_HEADER = [
  "track ID",
  "customer name",
  "customer phone No",
  "date",
  "price",
  "status",
  "finished",
  "products",
];

const baseVariables = (limit, skip) => ({
  limit,
  skip,
  createdAtSort: -1,
  status: undefined,
  createdAt: undefined,
  passengerName: undefined,
  passengerPhoneNumber: undefined,
  finalPrice: undefined,
  finalPriceSort: undefined,
  shipmentModel: undefined,
  tracking: { trackId: undefined, estimatedDelivery: undefined },
  finished: undefined,
  createdAtFrom: undefined,
});

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const isValidDate = (value) =>
  moment(value, "MM/DD/YYYY", true).isValid() ||
  moment(value, "MM-DD-YYYY", true).isValid() ||
  moment.isMoment(value);

const describeStatus = (status) =>
  STATUS_PRESENTATION[status] || {
    label: status ? status.replace(/_/g, " ").toLowerCase() : "unknown",
    tone: "neutral",
  };

// finalPrice and tracking can both be null on a partially created order;
// the previous version called .toFixed and .trackId on them unguarded,
// in the table and again in the export.
const money = (value) =>
  value === null || value === undefined ? "$0.00" : `$${value.toFixed(2)}`;

const productsToText = (order) =>
  order.cart && order.cart.products
    ? order.cart.products
        .map(
          (line) => `${line.product ? line.product.title : "?"}(${line.quantity})`,
        )
        .join(", ")
    : "";

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const OrdersHistory = () => {
  /* ---------------- paging & query variables ---------------- */

  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [pageCount, setPageCount] = useState(0);
  const [variables, setVariables] = useState(
    baseVariables(DEFAULT_PER_PAGE, 0),
  );

  const { loading, error, data, refetch } = useQuery(GET_ORDERS, {
    variables,
    fetchPolicy: "network-only",
  });

  const { data: countData, refetch: refetchCount } = useQuery(
    GET_ORDERS_COUNT,
    { variables, fetchPolicy: "network-only" },
  );

  const { data: summaryData, refetch: refetchSummary } = useQuery(
    GET_HISTORY_SUMMARY,
    { fetchPolicy: "network-only" },
  );

  /* ---------------- filter inputs ---------------- */

  const [trackIdFilter, setTrackIdFilter] = useState("");
  const [passengerNameFilter, setPassengerNameFilter] = useState("");
  const [passengerPhoneNumberFilter, setPassengerPhoneNumberFilter] =
    useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [status, setStatus] = useState(STATUS_OPTIONS[0]);
  const [shipmentModelFilter, setShipmentModelFilter] = useState(
    SHIPMENT_MODEL_OPTIONS[0],
  );
  const [finishedFilter, setFinishedFilter] = useState(FINISHED_OPTIONS[0]);

  const [trackIdFilterError, setTrackIdFilterError] = useState("");
  const [dateFromFilterError, setDateFromFilterError] = useState("");
  const [dateFilterError, setDateFilterError] = useState("");
  const [noFilterModal, setNoFilterModal] = useState(false);

  const [dateSort, setDateSort] = useState("down");
  const [priceSort, setPriceSort] = useState("");

  /* ---------------- dialog ---------------- */

  const [modal, setModal] = useState(false);
  const [orderDetail, setOrderDetail] = useState();
  const [copiedId, setCopiedId] = useState(null);

  /* ---------------- derived ---------------- */

  const list = useMemo(
    () => (data && data.getOrdersHistoryByShopAdmin) || [],
    [data],
  );

  const totalCount =
    (countData && countData.getOrdersHistoryByShopAdminCount) || 0;

  const pageTotal = useMemo(
    () => list.reduce((sum, order) => sum + (order.finalPrice || 0), 0),
    [list],
  );

  /* ---------------- filter validation ---------------- */

  useEffect(() => {
    if (!dateFromFilter) setDateFromFilterError("");
    else
      setDateFromFilterError(isValidDate(dateFromFilter) ? "" : "Invalid date");
  }, [dateFromFilter]);

  useEffect(() => {
    if (!dateFilter) setDateFilterError("");
    else setDateFilterError(isValidDate(dateFilter) ? "" : "Invalid date");
  }, [dateFilter]);

  useEffect(() => {
    setTrackIdFilterError(
      trackIdFilter && trackIdFilter.length !== TRACK_ID_LENGTH
        ? `Must be ${TRACK_ID_LENGTH} characters`
        : "",
    );
  }, [trackIdFilter]);

  /* ---------------- sorting ---------------- */

  useEffect(() => {
    setVariables((current) => ({
      ...current,
      createdAtSort:
        dateSort === "up" ? 1 : dateSort === "down" ? -1 : undefined,
      finalPriceSort:
        priceSort === "up" ? 1 : priceSort === "down" ? -1 : undefined,
    }));
  }, [dateSort, priceSort]);

  const onDateSortChange = () =>
    setDateSort((current) => (current === "up" ? "down" : "up"));
  const onPriceSortChange = () =>
    setPriceSort((current) => (current === "up" ? "down" : "up"));

  /* ---------------- paging ---------------- */

  useEffect(() => {
    setVariables((current) => ({
      ...current,
      limit: perPage,
      skip: pageCount * perPage,
    }));
  }, [pageCount, perPage]);

  /* ---------------- excel export ---------------- */

  const [excelRows, setExcelRows] = useState(null);
  const [showExcelButton, setShowExcelButton] = useState("firstStatus");
  const exportRequested = useRef(false);

  const [
    getOrdersHistoryByShopAdmin,
    { data: lazyData, loading: lazyLoading },
  ] = useLazyQuery(GET_ORDERS, { fetchPolicy: "network-only" });

  useEffect(() => {
    if (!exportRequested.current || lazyLoading || !lazyData) return;
    exportRequested.current = false;
    // Rebuilt from scratch each time. The old version appended to the
    // previous export's rows, so a second export contained the first.
    setExcelRows(
      lazyData.getOrdersHistoryByShopAdmin.map((item) => [
        item.tracking && item.tracking.trackId ? item.tracking.trackId : "-",
        item.user ? item.user.fullName : "-",
        item.user ? item.user.phoneNumber : "-",
        moment(item.createdAt).format("MM-DD-YYYY"),
        item.finalPrice === null || item.finalPrice === undefined
          ? "0.00"
          : item.finalPrice.toFixed(2),
        item.status ? item.status.replace(/_/g, " ").toLowerCase() : "-",
        item.finished ? "completed" : "in process",
        productsToText(item),
      ]),
    );
    setShowExcelButton("done");
  }, [lazyData, lazyLoading]);

  const onDownloadData = () => {
    if (!countData) return;
    exportRequested.current = true;
    setShowExcelButton("downloading");
    getOrdersHistoryByShopAdmin({
      variables: {
        ...variables,
        skip: 0,
        limit: Math.max(countData.getOrdersHistoryByShopAdminCount, 1),
      },
    });
  };

  const onExcelFinished = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([EXCEL_HEADER].concat(excelRows || []));
    XLSX.utils.book_append_sheet(wb, ws, "Orders history");
    XLSX.writeFile(wb, "OrdersHistory.xlsx");
  };

  const resetExport = () => {
    setExcelRows(null);
    setShowExcelButton("firstStatus");
    exportRequested.current = false;
  };

  /* ---------------- filter submit ---------------- */

  const hasAnyFilter = () =>
    Boolean(
      trackIdFilter ||
        variables.tracking.trackId ||
        passengerNameFilter ||
        variables.passengerName ||
        passengerPhoneNumberFilter ||
        variables.passengerPhoneNumber ||
        dateFromFilter ||
        variables.createdAtFrom ||
        dateFilter ||
        variables.createdAt ||
        !(status === "ALL" && !variables.status) ||
        !(finishedFilter === "ALL" && variables.finished === undefined) ||
        !(shipmentModelFilter === "ALL" && !variables.shipmentModel),
    );

  const onFilterSubmit = () => {
    if (!hasAnyFilter()) {
      setNoFilterModal(true);
      return;
    }

    resetExport();
    setPageCount(0);

    setVariables((current) => ({
      ...current,
      skip: 0,
      tracking:
        trackIdFilter && trackIdFilter.length === TRACK_ID_LENGTH
          ? { trackId: trackIdFilter.trim(), estimatedDelivery: undefined }
          : { trackId: undefined, estimatedDelivery: undefined },
      createdAtFrom:
        dateFromFilter && isValidDate(dateFromFilter)
          ? moment(dateFromFilter).format("YYYY-MM-DD")
          : undefined,
      createdAt:
        dateFilter && isValidDate(dateFilter)
          ? moment(dateFilter).format("YYYY-MM-DD")
          : undefined,
      passengerName: passengerNameFilter || undefined,
      passengerPhoneNumber: passengerPhoneNumberFilter || undefined,
      status:
        status === "ALL" ? undefined : status.replace(/ /g, "_").toUpperCase(),
      shipmentModel:
        shipmentModelFilter === "ALL" ? undefined : shipmentModelFilter,
      finished:
        finishedFilter === "ALL" ? undefined : finishedFilter === "FINISHED",
    }));
  };

  const onClearFilters = () => {
    resetExport();
    setPageCount(0);
    setVariables(baseVariables(perPage, 0));
    setTrackIdFilter("");
    setPassengerNameFilter("");
    setPassengerPhoneNumberFilter("");
    setDateFromFilter("");
    setDateFilter("");
    setStatus("ALL");
    // The old reset left these two behind, so a cleared form still
    // carried the previous completion filter and sort direction.
    setFinishedFilter("ALL");
    setShipmentModelFilter("ALL");
    setDateSort("down");
    setPriceSort("");
  };

  const handleEnter = (e) => {
    if (e.charCode === 13) onFilterSubmit();
  };

  /* ---------------- active filter chips ---------------- */

  const activeChips = [];
  if (variables.tracking.trackId)
    activeChips.push({
      id: "trackId",
      label: `Track: ${variables.tracking.trackId}`,
    });
  if (variables.passengerName)
    activeChips.push({ id: "name", label: `Name: ${variables.passengerName}` });
  if (variables.passengerPhoneNumber)
    activeChips.push({
      id: "phone",
      label: `Phone: ${variables.passengerPhoneNumber}`,
    });
  if (variables.createdAtFrom)
    activeChips.push({
      id: "dateFrom",
      label: `From: ${variables.createdAtFrom}`,
    });
  if (variables.createdAt)
    activeChips.push({ id: "dateTo", label: `To: ${variables.createdAt}` });
  if (variables.status)
    activeChips.push({
      id: "status",
      label: `Status: ${describeStatus(variables.status).label}`,
    });
  if (variables.finished !== undefined)
    activeChips.push({
      id: "finished",
      label: variables.finished ? "Completed" : "In progress",
    });
  if (variables.shipmentModel)
    activeChips.push({
      id: "shipment",
      label: `Delivery: ${variables.shipmentModel}`,
    });

  const clearChip = (id) => {
    resetExport();
    setPageCount(0);
    const patch = { skip: 0 };
    if (id === "trackId") {
      setTrackIdFilter("");
      patch.tracking = { trackId: undefined, estimatedDelivery: undefined };
    }
    if (id === "name") {
      setPassengerNameFilter("");
      patch.passengerName = undefined;
    }
    if (id === "phone") {
      setPassengerPhoneNumberFilter("");
      patch.passengerPhoneNumber = undefined;
    }
    if (id === "dateFrom") {
      setDateFromFilter("");
      patch.createdAtFrom = undefined;
    }
    if (id === "dateTo") {
      setDateFilter("");
      patch.createdAt = undefined;
    }
    if (id === "status") {
      setStatus("ALL");
      patch.status = undefined;
    }
    if (id === "finished") {
      setFinishedFilter("ALL");
      patch.finished = undefined;
    }
    if (id === "shipment") {
      setShipmentModelFilter("ALL");
      patch.shipmentModel = undefined;
    }
    setVariables((current) => ({ ...current, ...patch }));
  };

  const onRefresh = () => {
    refetch();
    refetchCount();
    refetchSummary();
  };

  /* ---------------- render ---------------- */

  if (error) return <ErrorPage />;

  const header = (
    <PageHeader
      title="Orders history"
      count={totalCount}
      description="Every order this shop has taken. Orders still in progress are on the Orders page."
      actions={
        <React.Fragment>
          <IconButton
            className="tableTopIconButton"
            size="small"
            onClick={onRefresh}
            disabled={loading}
            title="Refresh"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <PageActionButton
            variant="export"
            icon={<CloudDownloadIcon fontSize="small" />}
            label={
              showExcelButton === "downloading"
                ? "Preparing"
                : showExcelButton === "done"
                  ? "Download Excel"
                  : "Export"
            }
            info={
              showExcelButton === "downloading"
                ? "Fetching every match"
                : showExcelButton === "done"
                  ? `${excelRows ? excelRows.length : 0} rows ready`
                  : `${totalCount} matching orders`
            }
            loading={showExcelButton === "downloading"}
            onClick={
              showExcelButton === "done" ? onExcelFinished : onDownloadData
            }
          />
        </React.Fragment>
      }
    />
  );

  if (loading && !data) {
    return (
      <div className="page ordersHistoryPage">
        <main className="main">
          {header}
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={8} columns={6} />
        </main>
      </div>
    );
  }

  return (
    <div className="page ordersHistoryPage">
      <main className="main">
        {header}

        {/* SUMMARY */}
        <section className="stats">
          <MetricCard
            icon={<HistoryIcon />}
            title="All orders"
            value={summaryData ? summaryData.total : 0}
          />
          <MetricCard
            icon={<CheckCircleOutlineIcon />}
            title="Delivered"
            value={summaryData ? summaryData.delivered : 0}
            tone="success"
          />
          <MetricCard
            icon={<BlockIcon />}
            title="Rejected"
            value={summaryData ? summaryData.rejected : 0}
            tone={summaryData && summaryData.rejected > 0 ? "danger" : "default"}
          />
          <MetricCard
            icon={<AttachMoneyOutlinedIcon />}
            title="This page"
            value={money(pageTotal)}
            tone="info"
            hint={`${list.length} of ${totalCount} matching`}
          />
        </section>

        {/* FILTERS */}
        <section className="orderFilters" onKeyPress={handleEnter}>
          <Field
            label="Track ID"
            htmlFor="historyTrackIdFilter"
            error={trackIdFilter ? trackIdFilterError : ""}
          >
            <input
              id="historyTrackIdFilter"
              type="text"
              className="dsInput"
              placeholder="10-character ID"
              value={trackIdFilter}
              onChange={(e) => setTrackIdFilter(e.target.value)}
            />
          </Field>

          <Field label="Customer name" htmlFor="historyNameFilter">
            <input
              id="historyNameFilter"
              type="text"
              className="dsInput"
              placeholder="Name..."
              value={passengerNameFilter}
              onChange={(e) => setPassengerNameFilter(e.target.value)}
            />
          </Field>

          <Field label="Customer phone" htmlFor="historyPhoneFilter">
            <input
              id="historyPhoneFilter"
              type="text"
              className="dsInput"
              placeholder="Phone..."
              value={passengerPhoneNumberFilter}
              onChange={(e) => setPassengerPhoneNumberFilter(e.target.value)}
            />
          </Field>

          <Field label="Status">
            <FilterDropdown
              label={
                status === "ALL"
                  ? "All statuses"
                  : describeStatus(status.replace(/ /g, "_").toUpperCase())
                      .label
              }
              options={STATUS_OPTIONS.map((option) => ({
                value: option,
                label:
                  option === "ALL"
                    ? "All statuses"
                    : describeStatus(option.replace(/ /g, "_").toUpperCase())
                        .label,
              }))}
              value={status}
              active={status !== "ALL"}
              onChange={(e) => setStatus(e.target.value)}
            />
          </Field>

          <Field label="Completion">
            <FilterDropdown
              label={
                finishedFilter === "ALL"
                  ? "Any"
                  : finishedFilter === "FINISHED"
                    ? "Completed"
                    : "In progress"
              }
              options={FINISHED_OPTIONS.map((option) => ({
                value: option,
                label:
                  option === "ALL"
                    ? "Any"
                    : option === "FINISHED"
                      ? "Completed"
                      : "In progress",
              }))}
              value={finishedFilter}
              active={finishedFilter !== "ALL"}
              onChange={(e) => setFinishedFilter(e.target.value)}
            />
          </Field>

          <Field label="Delivery by">
            <FilterDropdown
              label={
                shipmentModelFilter === "ALL"
                  ? "Anyone"
                  : shipmentModelFilter === "SHOP"
                    ? "My shop"
                    : "Spark"
              }
              options={SHIPMENT_MODEL_OPTIONS.map((option) => ({
                value: option,
                label:
                  option === "ALL"
                    ? "Anyone"
                    : option === "SHOP"
                      ? "My shop"
                      : "Spark",
              }))}
              value={shipmentModelFilter}
              active={shipmentModelFilter !== "ALL"}
              onChange={(e) => setShipmentModelFilter(e.target.value)}
            />
          </Field>

          <Field
            label="Date range"
            error={dateFromFilterError || dateFilterError}
          >
            <div className="orderFilterPair">
              <Datetime
                disableOnClickOutside
                value={dateFromFilter}
                closeOnSelect
                onChange={(date) => setDateFromFilter(date)}
                timeFormat={false}
                inputProps={{ placeholder: "From" }}
              />
              <span className="orderFilterPairSeparator">to</span>
              <Datetime
                disableOnClickOutside
                value={dateFilter}
                closeOnSelect
                onChange={(date) => setDateFilter(date)}
                timeFormat={false}
                inputProps={{ placeholder: "To" }}
              />
            </div>
          </Field>

          <div className="orderFilterActions">
            <span className="dsFieldHint">
              Press Enter or Search to apply. Filters also drive the Excel
              export.
            </span>
            <Button onClick={onClearFilters}>Reset</Button>
            <Button variant="primary" onClick={onFilterSubmit}>
              Search
            </Button>
          </div>
        </section>

        {/* TOOLBAR */}
        <div className="tableTopBar">
          <div className="tableTopBarLeft">
            {activeChips.length > 0 ? (
              <ActiveFilterChipList
                filters={activeChips}
                onClearFilter={clearChip}
                onClearAll={onClearFilters}
              />
            ) : (
              <span className="dsFieldHint">
                Showing all past orders, newest first
              </span>
            )}
          </div>
        </div>

        {/* TABLE */}
        <div className="tableResponsive">
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Track ID</div>
              <div className="tableHeaderCell">Customer</div>
              <div className="tableHeaderCell">
                <button
                  type="button"
                  className={`orderSortButton ${dateSort ? "isActive" : ""}`}
                  onClick={onDateSortChange}
                  title="Sort by date"
                >
                  Date
                  {dateSort === "up" ? (
                    <ArrowUpwardIcon className="orderSortIcon" />
                  ) : dateSort === "down" ? (
                    <ArrowDownwardIcon className="orderSortIcon" />
                  ) : null}
                </button>
              </div>
              <div className="tableHeaderCell">
                <button
                  type="button"
                  className={`orderSortButton ${priceSort ? "isActive" : ""}`}
                  onClick={onPriceSortChange}
                  title="Sort by price"
                >
                  Price
                  {priceSort === "up" ? (
                    <ArrowUpwardIcon className="orderSortIcon" />
                  ) : priceSort === "down" ? (
                    <ArrowDownwardIcon className="orderSortIcon" />
                  ) : null}
                </button>
              </div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell">Products</div>
              <div className="tableHeaderCell tableHeaderAction">Details</div>
            </div>

            {list.length === 0 ? (
              <EmptyState
                icon={<HistoryIcon />}
                title={activeChips.length > 0 ? "No matches" : "Nothing here yet"}
                message={
                  activeChips.length > 0
                    ? "No past orders match these filters. Try widening or clearing them."
                    : "Completed and rejected orders will collect here."
                }
                action={
                  activeChips.length > 0 ? (
                    <Button onClick={onClearFilters}>Clear filters</Button>
                  ) : null
                }
              />
            ) : (
              list.map((order) => {
                const state = describeStatus(order.status);
                const trackId = order.tracking && order.tracking.trackId;
                return (
                  <div className="row" key={order._id}>
                    <span data-label="Track ID">
                      {trackId ? (
                        <CopyToClipboard
                          text={trackId}
                          onCopy={() => setCopiedId(order._id)}
                        >
                          <span
                            className={`orderTrackId ${
                              copiedId === order._id ? "isCopied" : ""
                            }`}
                            title="Click to copy"
                          >
                            {copiedId === order._id ? "Copied" : trackId}
                          </span>
                        </CopyToClipboard>
                      ) : (
                        <span className="orderTrackIdEmpty">—</span>
                      )}
                    </span>

                    <div className="orderCustomer" data-label="Customer">
                      <span className="orderCustomerName">
                        {order.user ? order.user.fullName : "—"}
                      </span>
                      <span className="orderCustomerPhone">
                        {order.user ? order.user.phoneNumber : ""}
                      </span>
                    </div>

                    <span className="updated" data-label="Date">
                      <strong>
                        {moment(order.createdAt).format("MMM D, YYYY")}
                      </strong>
                      <span>{moment(order.createdAt).format("HH:mm")}</span>
                    </span>

                    <span className="orderPrice" data-label="Price">
                      {money(order.finalPrice)}
                    </span>

                    <div className="orderStatusCell" data-label="Status">
                      <StatusBadge status={state.tone} label={state.label} />
                      <span
                        className={`orderCompletion ${
                          order.finished ? "isFinished" : "inProgress"
                        }`}
                        title={
                          order.finished
                            ? "Settled and closed"
                            : "Not yet settled"
                        }
                      >
                        {order.finished ? "Completed" : "In progress"}
                      </span>
                      {order.shipmentModel && (
                        <span
                          className="orderShipment"
                          title={`Delivered by ${order.shipmentModel}`}
                        >
                          {order.shipmentModel}
                        </span>
                      )}
                      {order.status === "REJECTED" && order.rejectedFor && (
                        <span className="orderRejectionReason">
                          {REJECTION_LABELS[order.rejectedFor] ||
                            order.rejectedFor}
                        </span>
                      )}
                    </div>

                    <div className="orderProducts" data-label="Products">
                      {order.cart && order.cart.products
                        ? order.cart.products.map((line, index) => (
                            <span
                              className="orderProductChip"
                              key={line.product ? line.product._id : index}
                              title={line.product ? line.product.title : ""}
                            >
                              <span className="orderProductQty">
                                {line.quantity}×
                              </span>
                              {line.product ? line.product.title : "?"}
                            </span>
                          ))
                        : null}
                    </div>

                    <div className="actions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => {
                          setOrderDetail(order);
                          setModal(true);
                        }}
                        title="Order details"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}

            {list.length > 0 && (
              <div className="orderTotalRow">
                <span className="orderTotalLabel">
                  Total on this page ({list.length}{" "}
                  {list.length === 1 ? "order" : "orders"})
                </span>
                <span className="orderTotalValue">{money(pageTotal)}</span>
              </div>
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={PER_PAGE_OPTIONS}
            onPageChange={(page) => setPageCount(page)}
            onPageSizeChange={(size) => {
              setPerPage(Number(size));
              setPageCount(0);
            }}
          />
        )}
      </main>

      {/* ORDER DETAILS */}
      <DialogShell
        open={modal}
        onClose={() => setModal(false)}
        title="Order details"
        subtitle={orderDetail ? `#${orderDetail._id.slice(-8).toUpperCase()}` : ""}
      >
        {orderDetail && <OrderDetails order={orderDetail} />}
      </DialogShell>

      {/* NO FILTER */}
      <DialogShell
        open={noFilterModal}
        onClose={() => setNoFilterModal(false)}
        title="Add a filter first"
        size="sm"
        message="Fill in at least one of the filters above before searching."
        actions={[
          {
            label: "Got it",
            onClick: () => setNoFilterModal(false),
            variant: "primary",
          },
        ]}
      />
    </div>
  );
};

export default OrdersHistory;
